import ApiService from "../services/api";
import { getAssetIdsByChassisListFromAssetSummary } from "../utils/assetUtils";
import { IAssetSummary } from "./fetchMaintenance";
import { Asset } from "../models/asset";
import { useRioPlansCache, useVolksTotalCache } from "../hooks/useAssets";
import { getUserProfile, UserProfile } from "../configuration/login/loginSlice";

export const fetchMaintenanceStats = async (
  persona: "MANAGER" | "TOWER" | "CONSULTANT" | "PARTNERSHIP",
  allAssets: Asset[]
): Promise<{
  availableToSchedule: string[];
  scheduled: string[];
  inProgress: string[];
  inProgressCount: number;
}> => {
  const apiService = new ApiService();

  try {
    const [scheduledResponse, inProgressResponse] = await Promise.all([
      apiService.get<any>(`/occurrence/${persona}/stats`),
      apiService.post<any>(`/occurrence/list/${persona}/time-spent/all`, {}),
    ]);

    const scheduled = scheduledResponse.data?.chassisListOccurrencesNotStarted ?? [];
    const inProgressAll = (inProgressResponse.data ?? []).filter((el: any) =>
      el.steps?.some((step: any) => !step.hasFinished)
    );
    const inProgressChassis = [...new Set(inProgressAll.map((el: any) => el.chassis))] as string[];

    return {
      availableToSchedule: allAssets
        .filter(
          (el) =>
            !scheduled.includes(el.chassis) && !inProgressChassis.includes(el.chassis)
        )
        .map((el) => el.chassis),
      scheduled: scheduled,
      inProgress: inProgressChassis,
      inProgressCount: inProgressChassis.length,
    };
  } catch (error) {
    console.error(error);
    return {
      availableToSchedule: [],
      scheduled: [],
      inProgress: [],
      inProgressCount: 0,
    };
  }
};

export const fetchMaintenanceDelayed = async (
  assets: { id: string; identification: string }[]
): Promise<string[]> => {
  const apiService = new ApiService();
  try {
    const response = await apiService.post<any>(
      `/big-numbers/vehicle/past-due-maintenance`,
      { assets }
    );
    return response.data.chassisList ?? [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const fetchAssetsByRioPlans = async (
  assets: IAssetSummary[],
  userProfile?: UserProfile
): Promise<{ [key: string]: string[] }> => {
  const apiService = new ApiService(import.meta.env.VITE_MARKETPLACE_API_URL);

  try {
    const assetsHash = JSON.stringify(
      assets
        .map((asset) => ({
          id: asset.id,
          identification: asset.identification,
        }))
        .sort((a, b) => a.id.localeCompare(b.id))
    );
    const cacheKey = `rio_plans_${btoa(assetsHash).slice(0, 50)}`;

    const cachedData = useRioPlansCache.getState().getCachedData(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    let assetIdsByPlans: { [key: string]: string[] } = {};

    const chunkSize = 50000;
    const promises = [];
    for (let i = 0; i < assets.length; i += chunkSize) {
      const chunkList = assets.slice(i, i + chunkSize);
      promises.push(
        apiService.post<any>(`/payment-gateway/subscription/active-assets-by-identification`, {
          identifications: chunkList.map((el) => el.identification),
        })
      );
    }

    const responses = await Promise.all(promises);
    
    const chassisToAssetIdMap = new Map<string, string>();
    assets.forEach((asset) => {
      chassisToAssetIdMap.set(asset.identification, asset.id);
    });

    responses.forEach((response: any) => {
      if (response.status === 200 && Array.isArray(response.data)) {
        response.data.filter((subscription: any) => 
          userProfile?.account === import.meta.env.VITE_CONTROL_TOWER_ID ||
          (subscription.account_id === userProfile?.account || !userProfile)
        ).forEach((subscription: any) => {
          const planName = subscription.product_name;
          const chassis = subscription.identification;
          const assetId = chassisToAssetIdMap.get(chassis);

          if (planName && assetId) {
            if (!assetIdsByPlans[planName]) {
              assetIdsByPlans[planName] = [];
            }
            
            if (!assetIdsByPlans[planName].includes(assetId)) {
              assetIdsByPlans[planName].push(assetId);
            }
          }
        });
      }
    });

    useRioPlansCache.getState().setCachedData(cacheKey, assetIdsByPlans);

    return assetIdsByPlans;
  } catch (error) {
    console.error(error);
    return {};
  }
};

export const fetchVolksTotalAssets = async (
  assets: IAssetSummary[]
): Promise<[number, string[], string[]]> => {
  const apiService = new ApiService();

  try {
    const assetsHash = JSON.stringify(
      assets
        .map((asset) => ({
          id: asset.id,
          identification: asset.identification,
        }))
        .sort((a, b) => a.id.localeCompare(b.id))
    );
    const cacheKey = `volks_total_${btoa(assetsHash).slice(0, 50)}`;

    const cachedData = useVolksTotalCache.getState().getCachedData(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    let total = 0;
    let volksTotalAssetIds: string[] = [];
    let withouVolksTotalAssetIds: string[] = [];

    const chunkSize = 50000;
    const promises = [];
    for (let i = 0; i < assets.length; i += chunkSize) {
      const chunk = assets.slice(i, i + chunkSize);
      promises.push(
        apiService.post<any>(`/big-numbers/vehicle/volks-total`, {
          assets: chunk
            .map((el) => ({ id: el.id, identification: el.identification }))
            .filter((el) => el.id && el.identification),
        })
      );
    }

    const responses = await Promise.all(promises);
    responses.forEach((response: any) => {
      if (response.status === 200) {
        total += response.data.total ?? 0;
        volksTotalAssetIds = [
          ...volksTotalAssetIds,
          ...getAssetIdsByChassisListFromAssetSummary(
            assets,
            response.data.chassisList
          ),
        ];
      }
    });

    withouVolksTotalAssetIds = assets
      .filter((el) => !volksTotalAssetIds.includes(el.id))
      .map((el) => el.id);

    const result: [number, string[], string[]] = [
      total,
      volksTotalAssetIds,
      withouVolksTotalAssetIds,
    ];

    useVolksTotalCache.getState().setCachedData(cacheKey, result);

    return result;
  } catch (error) {
    console.error(error);
    return [0, [], []];
  }
};
